import axios, { AxiosInstance } from 'axios';
import { API_DYNAVAULTS_BASE_URL } from 'utils/constants/common';

export interface DynaVault {
  id: number;
  address: `0x${string}`;
  chainId: number;
  asset: `0x${string}`;
  name: string;
  symbol: string;
  decimals: number;
  managementFees: number;
  performanceFee: number;
  ticker: string;
  factory: string;
  activation: string;
  managementWallet: `0x${string}` | '';
  governanceWallet: string;
  guardianWallet: string;
  functioningType: string;
  isClosed: boolean;
  metadata: {
    contracts: {
      type: string;
      DynaVault: `0x${string}`;
      DynaRouter: `0x${string}`;
      VaultZapper: `0x${string}`;
      dynaLiquidity: `0x${string}`;
      BalancerV2Zapper: `0x${string}`;
      DynaVaultManager: `0x${string}`;
    };
  };
  sinceInceptionAPY: number;
  twentyFourHourAPY: number;
  weeklyAPY: number;
  monthlyAPY: number;
  yearlyAPY: number;
  fees: {
    key: string;
    type: string;
    title: string;
    percentage: string;
  }[];
  type: 'YIELD' | 'INDEX';
  depositToken: `0x${string}`;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'USER_DEFINED';
  description: string;
  factsheets: string[];
  iconUrl: string;
  twentyFourHourPerformance: string;
  weeklyPerformance: string;
  monthlyPerformance: string;
  yearlyPerformance: string;
  sinceInceptionPerformance: string;
  apr: string;
  netPerformance: string;
  lastReport: string;
  lastComputed: string;
  totalSupply: string;
  price: number;
  availableFunds: string;
  deployedFunds: string;
  resourceId: number;
  createdAt: string;
  updatedAt: string;
  userWalletAddress: `0x${string}` | '';
  tokenAllocation?: { token: { address: `0x${string}` } }[];
}

export interface Token {
  symbol: string;
  name: string;
  decimals: number;
  address: `0x${string}`;
}

export interface VaultTransaction {
  sender: `0x${string}`;
  recipient: `0x${string}`;
  chainId: number;
  method: string;
  txnHash: `0x${string}`;
  timestamp: string;
  reasoning?: {
    title: string;
    details: string;
    token: string;
    action: string;
    reasoning: string;
  };
  metadata: {
    token_amount: string;
    minted_shares?: string;
    burned_shares?: string;
    deposit_token_amount?: string;
    deposit_token_address?: string;
    deposit_token_value_in_usd?: string;
    deposit_token?: {
      name: string;
      symbol: string;
      decimals: 6;
    };
    withdrawal_token_address?: string;
    withdrawal_token_amount?: string;
    withdrawal_token_value_in_usd?: string;
    withdrawal_token?: {
      name: string;
      symbol: string;
      decimals: 6;
    };
    token_in?: {
      name: string;
      symbol: string;
      decimals: 6;
      address: `0x${string}`;
    };
    amount_in?: string;
    amount_in_usd?: string;
  };
}

export interface DecisionReasoningResponse {
  decision: string;
  reasoning: {
    title: string;
    details: string;
  };
  id: number;
  vaultId: number;
  chainId: number;
  txnHash: string;
  timestamp: string;
}

export interface DynaVaultTokenAllocation {
  idle: string;
  debt: string;
  idleInUSD: string;
  token: {
    symbol: string;
    name: string;
    decimals: number;
    address: `0x${string}`;
  };
  metadata: {
    weight: string;
  };
}

export interface VaultStat {
  sharePrice: number;
  tvl: string;
  netPerformance: string;
  timestamp: string;
  sharePriceInUSD: number;
}

export interface RulesEngineIndicator {
  type: string;
  name: string;
  description: string;
}

export interface CreateVaultParams {
  userWalletAddress: `0x${string}`;
  tokens: `0x${string}`[];
  functioningType: 'DynaVault';
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'USER_DEFINED';
  chainId: number;
  resourceId: 2506;
  description: string;
  iconUrl: string;
  asset: `0x${string}`;
  name: string;
  symbol: string;
  isClosed: boolean;
  coolDownPeriod: number;
  watermarkPeriod: number;
  deviationPercent: number;
  initialDeposit?: {
    tokenOut: `0x${string}`;
    tokenIn: `0x${string}`;
    swapAmount: string;
  };
}

export interface CreateVaultResult {
  id: number;
  address: `0x${string}`;
  chainId: number;
}

export interface TradingRule {
  vaultId: number;
  name: string;
  description: string;
  active: boolean;
  priority?: number;
  conditions: Record<string, unknown>;
  action: {
    asset: `0x${string}`;
    targetWeight: number;
    reason: string;
  };
  symbol: string;
  timeframe: string;
}

export interface TokenPricesResponse {
  prices: Record<string, number>;
  updatedAt: string;
}

export class DynaVaultsAPI {
  private axiosInstance: AxiosInstance;

  constructor({ baseURL }: { baseURL: string }) {
    this.axiosInstance = axios.create({
      baseURL: baseURL,
    });
  }

  async loadVault(params: {
    address: `0x${string}`;
    chainId: number;
  }): Promise<DynaVault | null> {
    const { address, chainId } = params;
    const response = await this.axiosInstance.get(`/vaults/by-address`, {
      params: { address, chainId },
    });
    return response.data;
  }

  async loadVaults(authToken?: string): Promise<DynaVault[]> {
    try {
      const response = await this.axiosInstance.get(`/vaults`, {
        headers: authToken
          ? {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            }
          : undefined,
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async loadVaultTokenAllocation(params: {
    address: `0x${string}`;
    chainId: number;
  }): Promise<DynaVaultTokenAllocation[]> {
    const { address, chainId } = params;
    try {
      const response = await this.axiosInstance.get(
        `/vaults/token-allocation`,
        {
          params: { address, chainId },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [] as DynaVaultTokenAllocation[];
    }
  }

  async loadStats(params: {
    address: `0x${string}`;
    chainId: number;
    fromTimestamp: number;
    price: boolean;
    tvl: boolean;
  }): Promise<VaultStat[]> {
    const { address, chainId, fromTimestamp, price, tvl } = params;
    try {
      const response = await this.axiosInstance.get(`/vaults/stats`, {
        params: { address, chainId, fromTimestamp, price, tvl },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async loadVaultTransactions(params: {
    address: `0x${string}`;
    chainId: number;
  }): Promise<VaultTransaction[]> {
    const { address, chainId } = params;
    try {
      const response = await this.axiosInstance.get(`/vaults/transactions`, {
        params: { address, chainId },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async loadVaultDecisionReasoning(params: {
    address: `0x${string}`;
    chainId: number;
  }): Promise<DecisionReasoningResponse[]> {
    const { address, chainId } = params;
    try {
      const response = await this.axiosInstance.get(`/vaults/reasoning`, {
        params: { address, chainId },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async loadRulesEngineIndicators(): Promise<RulesEngineIndicator[]> {
    try {
      const response = await this.axiosInstance.get(`/rules-engine/indicators`);
      return response.data;
    } catch (error) {
      console.error('### loadRulesEngineIndicators error', error);
      return [];
    }
  }

  async createVault(
    vault: CreateVaultParams,
    authToken: string
  ): Promise<CreateVaultResult> {
    const response = await this.axiosInstance.post(`/vaults/create`, vault, {
      timeout: 5 * 60 * 1000,
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  async loadTokenPrices(): Promise<TokenPricesResponse> {
    const response =
      await this.axiosInstance.get<TokenPricesResponse>(`/prices`);
    return response.data;
  }

  async createTradingRule(rule: TradingRule, authToken: string) {
    const response = await this.axiosInstance.post(
      `/rules-engine/rules`,
      rule,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  }
}

export const dynaVaultsAPI = new DynaVaultsAPI({
  baseURL: API_DYNAVAULTS_BASE_URL,
});
